import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/services/api-client';
import { apiConfig } from '@/config/api';
import { useNavigate } from 'react-router-dom';

interface UserProfile {
  id: string;
  email: string;
  created_at: string;
  roles: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
  first_name?: string;
  last_name?: string;
  second_name?: string;
  job_title?: string;
  location?: string;
  photo_url?: string;
  is_active: boolean;
}

// Password validation function
const validatePassword = (password: string): string | null => {
  if (password.length < 8 || password.length > 100) {
    return 'Password must be between 8 and 100 characters long';
  }
  
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter (A-Z)';
  }
  
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter (a-z)';
  }
  
  if (!/[0-9]/.test(password)) {
    return 'Password must contain at least one digit (0-9)';
  }
  
  if (!/[!@#$%^&*()\-_=+\[\]{}|;:,.<>?/~]/.test(password)) {
    return 'Password must contain at least one special character (!@#$%^&*()-_=+[]{}|;:,.<>?/~)';
  }
  
  return null;
};

const ProfilePage = () => {
  const { changePassword } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Form state for editing
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    second_name: '',
    job_title: '',
    location: '',
  });

  // Password change form state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const loadProfile = async (showLoading = true) => {
    try {
      if (showLoading) {
        setIsLoading(true);
      }
      setError(null);
      console.log('[ProfilePage] Loading profile from:', apiConfig.endpoints.users.getProfile);
      const response = await apiClient.get(apiConfig.endpoints.users.getProfile);
      const profileData = response.data;
      console.log('[ProfilePage] Loaded profile data:', profileData);
      setProfile(profileData);
      // Initialize form data
      setFormData({
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        second_name: profileData.second_name || '',
        job_title: profileData.job_title || '',
        location: profileData.location || '',
      });
      console.log('[ProfilePage] Profile state updated, formData:', {
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        second_name: profileData.second_name || '',
        job_title: profileData.job_title || '',
        location: profileData.location || '',
      });
    } catch (err: any) {
      console.error('[ProfilePage] Failed to load profile:', err);
      setError('Failed to load profile information');
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
    setSaveSuccess(false);
    // Reset form data to original profile values
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        second_name: profile.second_name || '',
        job_title: profile.job_title || '',
        location: profile.location || '',
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      console.log('[ProfilePage] Sending update with data:', formData);

      // Build FormData payload (as API expects multipart/form-data)
      const formDataPayload = new FormData();
      
      // Always append all fields, even if empty (FastAPI can handle empty strings)
      formDataPayload.append('first_name', formData.first_name.trim() || '');
      formDataPayload.append('last_name', formData.last_name.trim() || '');
      formDataPayload.append('second_name', formData.second_name.trim() || '');
      formDataPayload.append('job_title', formData.job_title.trim() || '');
      formDataPayload.append('location', formData.location.trim() || '');

      console.log('[ProfilePage] FormData entries:');
      const entries: string[] = [];
      for (const [key, value] of formDataPayload.entries()) {
        console.log(`  ${key}: ${value}`);
        entries.push(`${key}=${value}`);
      }
      console.log('[ProfilePage] FormData has first_name:', formDataPayload.has('first_name'));
      console.log('[ProfilePage] FormData first_name value:', formDataPayload.get('first_name'));
      console.log('[ProfilePage] FormData toString:', entries.join('&'));

      const response = await apiClient.patch(
        apiConfig.endpoints.users.updateProfile, 
        formDataPayload
        // Don't set Content-Type - axios will set it automatically with boundary for FormData
      );

      console.log('[ProfilePage] Profile update response:', response);
      console.log('[ProfilePage] Response data:', response.data);
      console.log('[ProfilePage] Response status:', response.status);

      // Small delay to ensure server has processed the update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Reload profile from server to get updated data (without showing loading spinner)
      console.log('[ProfilePage] Reloading profile...');
      await loadProfile(false);
      console.log('[ProfilePage] Profile reloaded');
      
      setIsEditing(false);
      setSaveSuccess(true);
      
      // Force a re-render by updating a dummy state
      setProfile((prev) => ({ ...prev! }));
      
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err: any) {
      console.error('[ProfilePage] Failed to update profile:', err);
      console.error('[ProfilePage] Error response:', err?.response);
      const errorMessage = err?.response?.data?.detail || 
                          err?.response?.data?.message || 
                          'Failed to update profile';
      setSaveError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all fields');
      return;
    }

    // Validate new password requirements
    const passwordValidationError = validatePassword(newPassword);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (oldPassword === newPassword) {
      setPasswordError('New password must be different from the old password');
      return;
    }

    setIsChangingPassword(true);

    try {
      const successResult = await changePassword(oldPassword, newPassword);
      if (successResult) {
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordSuccess(true);
        setPasswordError('');
        // After password change, user needs to re-login with new password
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Password changed successfully. Please login with your new password.' 
            } 
          });
        }, 1500);
      }
    } catch (err: any) {
      console.error('[ProfilePage] Change password error:', err);
      
      // Check if it's a validation error for new password
      const errorDetail = err?.response?.data?.detail || '';
      const errorMessage = err?.response?.data?.message || '';
      const status = err?.response?.status;
      
      // Check if error is about password requirements (validation error)
      if (status === 422 || status === 400) {
        const errorText = (errorDetail || errorMessage || '').toLowerCase();
        if (errorText.includes('password') && 
            (errorText.includes('requirement') || 
             errorText.includes('must contain') || 
             errorText.includes('uppercase') || 
             errorText.includes('lowercase') || 
             errorText.includes('digit') || 
             errorText.includes('special') ||
             errorText.includes('length') ||
             errorText.includes('character'))) {
          // It's a password validation error
          setPasswordError(errorDetail || errorMessage || 'New password does not meet requirements');
        } else {
          // Other validation error
          setPasswordError(errorDetail || errorMessage || 'Validation error. Please check your input.');
        }
      } else if (status === 401 || status === 403) {
        // Authentication error - likely wrong old password
        setPasswordError('Current password is incorrect. Please try again.');
      } else {
        // Other error
        setPasswordError(errorDetail || errorMessage || 'An error occurred while changing password');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getDisplayName = () => {
    if (profile?.first_name) return profile.first_name;
    // Always use part of email before @, never full email
    if (profile?.email) return profile.email.split('@')[0];
    return 'User';
  };

  if (isLoading) {
    return (
      <div className="page-container" data-test-id="profile-page-container">
        <div className="page-inner" data-test-id="profile-page-inner">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container" data-test-id="profile-page-container">
        <div className="page-inner" data-test-id="profile-page-inner">
          <Card className="w-full max-w-2xl mx-auto">
            <CardContent className="pt-6">
              <div className="text-center text-red-600">{error}</div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-test-id="profile-page-container">
      <div className="page-inner" data-test-id="profile-page-inner">
        <div className="mb-8" data-test-id="profile-header">
          <h1 className="text-4xl font-bold mb-2 text-gray-900">Hello, {getDisplayName()}!</h1>
          <p className="text-lg text-gray-600">
            Your role is:{' '}
            <span className="text-blue-600 font-semibold">
              {profile?.roles && profile.roles.length > 0
                ? profile.roles.map((r) => r.name).join(', ')
                : 'User'}
            </span>
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Profile Information */}
          <Card className="w-full" data-test-id="profile-info-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Profile Information</CardTitle>
              {!isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  data-test-id="edit-profile-button"
                >
                  Edit
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {profile && (
                <>
                  {/* Email - read only */}
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Email
                    </Label>
                    <p className="text-base text-gray-900 mt-1">
                      {profile.email}
                    </p>
                  </div>

                  {/* First Name */}
                  <div>
                    <Label htmlFor="first_name" className="text-sm font-medium text-gray-500">
                      First Name
                    </Label>
                    {isEditing ? (
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="mt-1"
                        data-test-id="first-name-input"
                      />
                    ) : (
                      <p className="text-base text-gray-900 mt-1">
                        {profile.first_name || '-'}
                      </p>
                    )}
                  </div>

                  {/* Second Name */}
                  <div>
                    <Label htmlFor="second_name" className="text-sm font-medium text-gray-500">
                      Middle Name
                    </Label>
                    {isEditing ? (
                      <Input
                        id="second_name"
                        value={formData.second_name}
                        onChange={(e) => setFormData({ ...formData, second_name: e.target.value })}
                        className="mt-1"
                        data-test-id="second-name-input"
                      />
                    ) : (
                      <p className="text-base text-gray-900 mt-1">
                        {profile.second_name || '-'}
                      </p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <Label htmlFor="last_name" className="text-sm font-medium text-gray-500">
                      Last Name
                    </Label>
                    {isEditing ? (
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="mt-1"
                        data-test-id="last-name-input"
                      />
                    ) : (
                      <p className="text-base text-gray-900 mt-1">
                        {profile.last_name || '-'}
                      </p>
                    )}
                  </div>

                  {/* Job Title */}
                  <div>
                    <Label htmlFor="job_title" className="text-sm font-medium text-gray-500">
                      Job Title
                    </Label>
                    {isEditing ? (
                      <Input
                        id="job_title"
                        value={formData.job_title}
                        onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                        className="mt-1"
                        data-test-id="job-title-input"
                      />
                    ) : (
                      <p className="text-base text-gray-900 mt-1">
                        {profile.job_title || '-'}
                      </p>
                    )}
                  </div>

                  {/* Location */}
                  <div>
                    <Label htmlFor="location" className="text-sm font-medium text-gray-500">
                      Location
                    </Label>
                    {isEditing ? (
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        className="mt-1"
                        data-test-id="location-input"
                      />
                    ) : (
                      <p className="text-base text-gray-900 mt-1">
                        {profile.location || '-'}
                      </p>
                    )}
                  </div>

                  {/* Role - read only */}
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Role
                    </Label>
                    <p className="text-base text-gray-900 mt-1">
                      {profile.roles && profile.roles.length > 0
                        ? profile.roles.map((r) => r.name).join(', ')
                        : 'User'}
                    </p>
                  </div>

                  {/* Account Status - read only */}
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Account Status
                    </Label>
                    <p className="text-base text-gray-900 mt-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          profile.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {profile.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>

                  {/* Member Since - read only */}
                  <div>
                    <Label className="text-sm font-medium text-gray-500">
                      Member Since
                    </Label>
                    <p className="text-base text-gray-900 mt-1">
                      {formatDate(profile.created_at)}
                    </p>
                  </div>

                  {/* Edit buttons */}
                  {isEditing && (
                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex-1"
                        data-test-id="save-profile-button"
                      >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isSaving}
                        data-test-id="cancel-edit-button"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}

                  {/* Success/Error messages */}
                  {saveSuccess && (
                    <div className="text-sm text-green-600 bg-green-50 p-2 rounded mt-2">
                      Profile updated successfully!
                    </div>
                  )}
                  {saveError && (
                    <div className="text-sm text-red-600 bg-red-50 p-2 rounded mt-2">
                      {saveError}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Change Password - без вложенной Card */}
          <div className="w-full" data-test-id="change-password-section">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Change Password</h2>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="old-password">Current Password</Label>
                <Input
                  id="old-password"
                  type="password"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  disabled={isChangingPassword || passwordSuccess}
                  required
                  autoComplete="current-password"
                  className="mt-1"
                  data-test-id="old-password-input"
                />
              </div>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isChangingPassword || passwordSuccess}
                  required
                  autoComplete="new-password"
                  minLength={8}
                  className="mt-1"
                  data-test-id="new-password-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be 8-100 characters with uppercase, lowercase, digit, and special character
                </p>
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isChangingPassword || passwordSuccess}
                  required
                  autoComplete="new-password"
                  minLength={8}
                  className="mt-1"
                  data-test-id="confirm-password-input"
                />
              </div>
              {passwordError && (
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="text-sm text-green-600 bg-green-50 p-2 rounded">
                  Password changed successfully! Redirecting to login...
                </div>
              )}
              <Button
                type="submit"
                disabled={isChangingPassword || passwordSuccess}
                className="w-full"
                data-test-id="change-password-button"
              >
                {isChangingPassword ? 'Changing...' : passwordSuccess ? 'Redirecting...' : 'Change Password'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;

